import { canRefreshAfter } from '@/lib/auth/sessionErrors';

export class ApiError extends Error {
  readonly status: number;
  readonly data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }

  /** True when the endpoint itself is missing/not deployed rather than the request being wrong. */
  get isEndpointMissing(): boolean {
    return this.status === 404 || this.status === 501;
  }

  /** True when the request never reached the server (offline, DNS, CORS). */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Send cookies — required if the API issues a session cookie on login. */
  credentials?: RequestCredentials;
}

const DEFAULT_TIMEOUT_MS = 20_000;

function looksLikeHtml(value: string): boolean {
  const trimmed = value.trimStart().toLowerCase();
  return trimmed.startsWith('<!doctype') || trimmed.startsWith('<html') || trimmed.includes('<body');
}

function extractMessage(payload: unknown, fallback: string): string {
  // Nginx / Next error pages are HTML — never surface that blob in the UI.
  if (typeof payload === 'string' && payload.trim()) {
    return looksLikeHtml(payload) ? fallback : payload;
  }

  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    for (const key of ['message', 'detail', 'error']) {
      const value = record[key];
      if (typeof value === 'string' && value.trim() && !looksLikeHtml(value)) return value;
    }

    // Tablet-app style `{ errors: [{ name, message }] }`
    const errors = record.errors;
    if (Array.isArray(errors)) {
      for (const entry of errors) {
        if (typeof entry === 'string' && entry.trim() && !looksLikeHtml(entry)) return entry.trim();
        if (entry && typeof entry === 'object') {
          const message = (entry as Record<string, unknown>).message;
          if (typeof message === 'string' && message.trim() && !looksLikeHtml(message)) {
            return message.trim();
          }
        }
      }
    }

    // DRF-style `{ field: ["msg"] }`
    for (const value of Object.values(record)) {
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    }
  }

  return fallback;
}

/**
 * Called when an authenticated request gets a 401. `expired` → try a refresh and resolve
 * to the new access token (or null); `ended` (idle timeout / revoked) → sign out, resolve null.
 * Registered by the auth layer.
 */
export type UnauthorizedReason = 'expired' | 'ended';
export type UnauthorizedHandler = (reason: UnauthorizedReason) => Promise<string | null>;

let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

interface Sent {
  response: Response;
  payload: unknown;
}

async function send(url: string, options: RequestOptions): Promise<Sent> {
  const { method = 'GET', body, headers = {}, signal, timeoutMs = DEFAULT_TIMEOUT_MS, credentials } = options;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  if (signal) signal.addEventListener('abort', () => controller.abort(), { once: true });

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      credentials,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    throw new ApiError(
      aborted ? 'The request timed out. Please check your connection and try again.' : 'Network error. Please try again.',
      0,
      error,
    );
  } finally {
    clearTimeout(timeout);
  }

  const text = await response.text();
  let payload: unknown = text;
  if (text) {
    try {
      payload = JSON.parse(text) as unknown;
    } catch {
      // Non-JSON responses (HTML error pages) stay as raw text.
    }
  }
  return { response, payload };
}

/**
 * Thin `fetch` wrapper: JSON in/out, request timeouts, and errors normalised to
 * {@link ApiError} so UI code branches on status instead of parsing strings.
 * An authenticated call that hits an expired token is refreshed and retried once.
 */
export async function request<TResponse>(url: string, options: RequestOptions = {}): Promise<TResponse> {
  let { response, payload } = await send(url, options);

  const hasBearer = Boolean(options.headers?.Authorization);
  if (hasBearer && unauthorizedHandler && response.status === 401) {
    const token = await unauthorizedHandler(canRefreshAfter(response.status, payload) ? 'expired' : 'ended');
    if (token) {
      ({ response, payload } = await send(url, {
        ...options,
        headers: { ...options.headers, Authorization: `Bearer ${token}` },
      }));
    }
  }

  if (!response.ok) {
    throw new ApiError(extractMessage(payload, `Request failed (${response.status})`), response.status, payload);
  }

  return payload as TResponse;
}
